<template>
	<v-container class="mx-auto">
		<v-row class="main-content-container mx-auto" v-if="quiz">
			<v-col class="mb-4 col-lg-8 col-12">
				<v-card class="mx-auto px-4" max-width="100%">
					<v-card-title>{{ quiz.name }}</v-card-title>
					<v-col class="pl-0">
						<ul class="no-bullet-point">
							<li
								v-for="(question, index) in quiz.questions"
								:key="index"
								:ref="`section-${index}`"
							>
								<v-row class="py-2">
									<v-col class="col-1 pa-0" style="max-width: 40px">
										<span class="mr-2 float-left">{{ index + 1 }}</span>
									</v-col>
									<v-col class="pa-0">
										<v-card-text class="pa-0">
											<span class="inner-li">{{ question.question }}</span>
										</v-card-text>
										<template v-if="question.type == 'SELECTONE'">
											<div fluid>
												<v-radio-group
													v-model="answers[index]['answer']"
													column
												>
													<v-radio
														v-for="i in [1, 2, 3, 4]"
														:key="i"
														size="3"
														ripple
														class="font-small"
														:label="question[`option_${i}`]"
														:value="question[`option_${i}`]"
													></v-radio>
												</v-radio-group>
											</div>
										</template>

										<template v-if="question.type == 'SELECTMULTIPLE'">
											<div fluid class="mt-8">
												<v-checkbox
													v-for="i in [1, 2, 3, 4]"
													:key="i"
													size="3"
													ripple
													class="font-small pa-0 ma-0 mt-n3"
													:label="question[`option_${i}`]"
													:value="`option_${i}`"
													v-model="answers[index]['answer']"
												></v-checkbox>
											</div>
										</template>

										<template v-if="question.type == 'FILL'">
											<v-col cols="12" sm="6" md="4" class="pa-0">
												<v-text-field
													class="font-small"
													v-model="answers[index]['answer']"
												></v-text-field>
											</v-col>
										</template>

										<template v-if="question.type == 'TRUEFALSE'">
											<div fluid>
												<v-radio-group
													v-model="answers[index]['answer']"
													column
												>
													<v-radio
														size="3"
														ripple
														class="font-small"
														label="True"
														:value="true"
													></v-radio>
													<v-radio
														size="3"
														ripple
														class="font-small"
														label="False"
														:value="false"
													></v-radio>
												</v-radio-group>
											</div>
										</template>

										<template v-if="question.type == 'RANK'">
											<draggable
												v-model="answers[index]['answer']"
												class="pt-2 draggable"
											>
												<transition-group>
													<v-card
														:elevation="0"
														class="max-500 cursor-pointer"
														v-for="(item, inner_index) in answers[index][
															'answer'
														]"
														:value="question[`option_${inner_index}`]"
														ghost-class="ghost"
														:key="`answer${inner_index}`"
													>
														<v-hover>
															<template v-slot="{ hover }">
																<div
																	:class="`elevation-${hover ? 3 : 0}`"
																	class="transition-swing rounded-lg pa-3 font-small ma-2 mx-0 truncated-text draggable-card"
																>
																	<v-icon class="mr-2 float-left col-lightgrey"
																		>mdi-drag</v-icon
																	>
																	{{ question[item] }}
																</div>
															</template>
														</v-hover>
													</v-card>
												</transition-group>
											</draggable>
										</template>
										<v-expansion-panels
											flat
											hover
											class="border font-small max-500 mt-3"
											v-if="
												quiz.has_completed || question.tip.includes('TEST MODE')
											"
										>
											<v-expansion-panel class="pa-n7">
												<v-expansion-panel-header class="font-small"
													>See tip</v-expansion-panel-header
												>
												<v-expansion-panel-content class="font-small">
													{{ question.tip }}
												</v-expansion-panel-content>
											</v-expansion-panel>
										</v-expansion-panels>
									</v-col>
								</v-row>
							</li>
						</ul>
					</v-col>

					<v-card-actions class="justify-end">
						<v-btn
							class="mt-2 text-capitalize"
							:color="theme.primary"
							@click="goUp"
							width="120"
							depressed
							text
						>
							<v-icon class="mr-2">mdi-chevron-up</v-icon>
							Go up
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-col>
			<v-col class="mb-4 col-md-4">
				<v-card class="mx-auto" max-width="100%">
					<v-card-title class="font-normal">{{
						quiz.school.name
					}}</v-card-title>

					<v-card-subtitle class="pb-0 pt-1 font-small"
						>Last updated {{ formatDate(quiz.updated_at) }}</v-card-subtitle
					>

					<v-card-subtitle class="font-small">{{
						`Grade ${quiz.grade} • Level ${quiz.difficulty}`
					}}</v-card-subtitle>

					<v-row>
						<v-col class="col-md-5 text-center">
							<v-chip class="duration ml-5">
								<v-icon id="icon">mdi-alarm</v-icon>
								<span class="ml-3">
									{{ formatDuration(quiz.duration) }}
								</span>
							</v-chip>
						</v-col>
						<v-col class="col-md-7 text-center">
							<v-chip class="subject">
								<v-icon
									id="icon"
									:style="{
										color: `${quiz.subject.color} !important`,
									}"
									>mdi-label</v-icon
								>
								<span
									class="ml-3"
									:style="{
										color: `${quiz.subject.color} !important`,
									}"
								>
									{{ quiz.subject.name }}
								</span>
							</v-chip>
						</v-col>
					</v-row>
					<template>
						<v-stepper :value="step_value" vertical>
							<v-stepper-items>
								<div v-for="(question, index) in quiz.questions" :key="index">
									<v-stepper-step
										@click="goToSection(index)"
										class="font-small stepper"
										:step="index + 1"
									>
										<span class="truncated-text font-small">{{
											question.question
										}}</span>
									</v-stepper-step>
								</div>
							</v-stepper-items>
						</v-stepper>
					</template>

					<v-card-text class="text--success" v-if="quiz.has_completed">
						<div>
							You have completed this quiz on
							{{ formatDate(quiz.previous_attempt.updated_at) }}. <br /><br />
							<div class="text-center">
								<v-chip class="score text-center">
									<span class="pa-2 score">
										You got {{ quiz.previous_attempt.score }}
									</span>
								</v-chip>
							</div>
						</div>
					</v-card-text>
					<v-card-actions class="mx-auto">
						<v-btn
							class="white--text mt-2 text-capitalize"
							:color="theme.primary"
							@click="complete"
							width="100%"
							:disabled="can_submit == false"
							depressed
						>
							Submit
							<v-icon class="ml-2">mdi-chevron-right</v-icon>
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import draggable from 'vuedraggable'
export default {
	name: 'Quiz',
	components: {
		draggable,
	},

	data: () => ({
		quiz: null,
		answers: [],
	}),

	methods: {
		...mapMutations(['setAlert', 'setUser']),
		goToSection(index) {
			this.$nextTick(() => {
				this.$refs[`section-${index}`][0].scrollIntoView({ behavior: 'smooth' })
			})
		},
		formatDate(date) {
			return this.core.moment(date).format('llll')
		},
		formatDuration(duration) {
			return this.core.moment
				.utc(this.core.moment.duration(duration, 'minutes').asMilliseconds())
				.format('HH[h] mm[m]')
		},
		complete() {
			this.core.http
				.post(`quiz/${this.$route.params.id}/complete`, {
					answers: this.answers,
				})
				.then(response => {
					this.setUser(response.data.user)

					this.setAlert(response)

					setTimeout(() => {
						this.$router.push({ name: 'Dashboard' })
					}, 1000)
				})
				.catch(error => {
					this.setAlert(error.response)
				})
		},
		goUp() {
			window.scrollTo({
				top: 0,
				left: 0,
				behavior: 'smooth',
			})
		},

		formatAnswers() {
			this.answers = this.quiz.questions.map(q => {
				let base_answer
				if (q.type == 'SELECTONE') base_answer = ''
				if (q.type == 'SELECTMULTIPLE') base_answer = []
				if (q.type == 'FILL') base_answer = ''
				if (q.type == 'TRUEFALSE') base_answer = null
				else if (q.type == 'RANK')
					base_answer = ['option_1', 'option_2', 'option_3', 'option_4']

				return { question_id: q.id, answer: base_answer }
			})
		},
	},

	mounted() {
		this.core.http
			.get(`quiz/${this.$route.params.id}`)
			.then(response => {
				this.quiz = response.data.quiz
				this.formatAnswers()
			})
			.catch(error => {
				this.setAlert(error.response)
			})
	},

	computed: {
		...mapGetters(['theme', 'user']),
		step_value: function() {
			return 0
		},
		can_submit: function() {
			return this.answers.every(a => {
				let ans = a.answer
				if (typeof ans == 'boolean') ans = ans.toString()
				return ans != null && ans != []
			})
		},
	},
}
</script>

<style scoped>
.font-small >>> label {
	font-size: 0.8rem;
}
.v-radio i {
	font-size: 48px;
}
.draggable {
	cursor: pointer;
}
.draggable-card {
	border: 1px solid lightgrey;
	max-width: 500px;
}
.max-500 {
	max-width: 500px;
}
.v-expansion-panel-header {
	min-height: 35px !important;
}
.v-expansion-panel-header {
	padding: 13px 20px !important;
}
.max-500 {
	max-width: 500px;
}
</style>
